import { createSlice } from "@reduxjs/toolkit"
import {
  saGetAllTenants,
  saGetTenantDetails,
  saCreateTenant,
  saUpdateTenant,
  saUpdateTenantStatus,
  saGetTenantInvoices,
  saGetTenantSubscription,
  saAssignTenantPlan,
  saGetAllPlans,
  saGetPlanDetails,
  saCreatePlan,
  saUpdatePlan,
  saDeletePlan,
  saUpdatePlanStatus,
  saGetAllSubscriptions,
  saGetAllInvoices,
  saGetAllIntegrationCategories,
  saCreateIntegrationCategory,
  saUpdateIntegrationCategory,
  saDeleteIntegrationCategory,
  saUpdateIntegrationCategoryStatus,
  saGetAllIntegrations,
  saGetIntegrationsCatalogTree,
  saCreateIntegration,
  saUpdateIntegration,
  saDeleteIntegration,
  saUpdateIntegrationStatus,
  saRefreshTenantPermissions,
  saStartTenantMigration,
  saGetTenantMigrationStatus,
  saCompleteTenantMigrationCutover,
} from "store/actions/superAdminAction"

const initialState = {
  // Tenants
  tenantsLoading: false,
  tenantActionLoading: false,
  tenants: [],
  tenantDetails: {},
  tenantSubscription: null,
  tenantInvoices: [],
  tenantsTotalCount: 0,
  tenantsTotalPages: null,
  tenantsCurrentPage: 1,
  tenantMigrationStatus: null,
  tenantMigrationLoading: false,
  tenantMigrationActionLoading: false,

  // Plans (superadmin-managed)
  plansLoading: false,
  planActionLoading: false,
  plans: [],
  planDetails: {},
  plansTotalCount: 0,
  plansTotalPages: null,
  plansCurrentPage: 1,

  // Billing oversight — subscriptions
  subscriptionsLoading: false,
  subscriptions: [],
  subscriptionsTotalCount: 0,
  subscriptionsTotalPages: null,
  subscriptionsCurrentPage: 1,

  // Billing oversight — invoices
  invoicesLoading: false,
  invoices: [],
  invoicesTotalCount: 0,
  invoicesTotalPages: null,
  invoicesCurrentPage: 1,

  // Superadmin integrations
  integrationCategoriesLoading: false,
  integrationCategoriesActionLoading: false,
  integrationCategories: [],
  integrationsLoading: false,
  integrationsActionLoading: false,
  integrations: [],
  integrationCatalogTreeLoading: false,
  integrationCatalogTree: [],

  error: null,
}

const superAdminSlice = createSlice({
  name: "superAdmin",
  initialState,
  reducers: {
    clearTenantDetails(state) {
      state.tenantDetails = {}
      state.tenantSubscription = null
      state.tenantInvoices = []
      state.tenantMigrationStatus = null
    },
    clearPlanDetails(state) {
      state.planDetails = {}
    },
  },
  extraReducers: builder => {
    // ── GET ALL TENANTS ────────────────────────────────────────────────────────
    builder
      .addCase(saGetAllTenants.pending, state => {
        state.tenantsLoading = true
        state.error = null
      })
      .addCase(saGetAllTenants.fulfilled, (state, action) => {
        state.tenantsLoading = false
        const { status, data, totalCount, totalPages, currentPage } =
          action.payload || {}
        if (status) {
          state.tenants = Array.isArray(data) ? data : []
          state.tenantsTotalCount = totalCount || 0
          state.tenantsTotalPages = totalPages || null
          state.tenantsCurrentPage = currentPage || 1
        }
      })
      .addCase(saGetAllTenants.rejected, state => {
        state.tenantsLoading = false
      })

    // ── GET TENANT DETAILS ─────────────────────────────────────────────────────
    builder
      .addCase(saGetTenantDetails.pending, state => {
        state.tenantsLoading = true
        state.tenantDetails = {}
      })
      .addCase(saGetTenantDetails.fulfilled, (state, action) => {
        state.tenantsLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.tenantDetails = data || {}
        }
      })
      .addCase(saGetTenantDetails.rejected, state => {
        state.tenantsLoading = false
      })

    // ── CREATE TENANT ──────────────────────────────────────────────────────────
    builder
      .addCase(saCreateTenant.pending, state => {
        state.tenantActionLoading = true
      })
      .addCase(saCreateTenant.fulfilled, state => {
        state.tenantActionLoading = false
      })
      .addCase(saCreateTenant.rejected, state => {
        state.tenantActionLoading = false
      })

    // ── UPDATE TENANT ──────────────────────────────────────────────────────────
    builder
      .addCase(saUpdateTenant.pending, state => {
        state.tenantActionLoading = true
      })
      .addCase(saUpdateTenant.fulfilled, state => {
        state.tenantActionLoading = false
      })
      .addCase(saUpdateTenant.rejected, state => {
        state.tenantActionLoading = false
      })

    // ── TENANT DATA MIGRATION ──────────────────────────────────────────────────
    builder
      .addCase(saGetTenantMigrationStatus.pending, state => {
        state.tenantMigrationLoading = true
      })
      .addCase(saGetTenantMigrationStatus.fulfilled, (state, action) => {
        state.tenantMigrationLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.tenantMigrationStatus = data || null
        }
      })
      .addCase(saGetTenantMigrationStatus.rejected, state => {
        state.tenantMigrationLoading = false
      })

    builder
      .addCase(saStartTenantMigration.pending, state => {
        state.tenantMigrationActionLoading = true
      })
      .addCase(saStartTenantMigration.fulfilled, (state, action) => {
        state.tenantMigrationActionLoading = false
        const { status, data } = action.payload || {}
        if (status && data) {
          state.tenantMigrationStatus = data
        }
      })
      .addCase(saStartTenantMigration.rejected, state => {
        state.tenantMigrationActionLoading = false
      })

    builder
      .addCase(saCompleteTenantMigrationCutover.pending, state => {
        state.tenantMigrationActionLoading = true
      })
      .addCase(saCompleteTenantMigrationCutover.fulfilled, (state, action) => {
        state.tenantMigrationActionLoading = false
        const { status, data } = action.payload || {}
        if (status && data) {
          state.tenantMigrationStatus = data
        }
      })
      .addCase(saCompleteTenantMigrationCutover.rejected, state => {
        state.tenantMigrationActionLoading = false
      })

    // ── UPDATE TENANT STATUS ───────────────────────────────────────────────────
    builder
      .addCase(saUpdateTenantStatus.pending, state => {
        state.tenantActionLoading = true
      })
      .addCase(saUpdateTenantStatus.fulfilled, (state, action) => {
        state.tenantActionLoading = false
        const { status } = action.payload || {}
        if (status) {
          const { id, status: tenantStatus } = action.meta.arg
          state.tenants = state.tenants.map(t =>
            t._id === id ? { ...t, status: tenantStatus } : t
          )
        }
      })
      .addCase(saUpdateTenantStatus.rejected, state => {
        state.tenantActionLoading = false
      })

    builder
      .addCase(saRefreshTenantPermissions.pending, state => {
        state.tenantActionLoading = true
      })
      .addCase(saRefreshTenantPermissions.fulfilled, state => {
        state.tenantActionLoading = false
      })
      .addCase(saRefreshTenantPermissions.rejected, state => {
        state.tenantActionLoading = false
      })

    // ── GET TENANT INVOICES ────────────────────────────────────────────────────
    builder
      .addCase(saGetTenantInvoices.pending, state => {
        state.tenantsLoading = true
      })
      .addCase(saGetTenantInvoices.fulfilled, (state, action) => {
        state.tenantsLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.tenantInvoices = Array.isArray(data) ? data : []
        }
      })
      .addCase(saGetTenantInvoices.rejected, state => {
        state.tenantsLoading = false
      })

    // ── GET TENANT SUBSCRIPTION ────────────────────────────────────────────────
    builder
      .addCase(saGetTenantSubscription.pending, state => {
        state.tenantsLoading = true
      })
      .addCase(saGetTenantSubscription.fulfilled, (state, action) => {
        state.tenantsLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.tenantSubscription = data || null
        }
      })
      .addCase(saGetTenantSubscription.rejected, state => {
        state.tenantsLoading = false
      })

    // ── ASSIGN TENANT PLAN ─────────────────────────────────────────────────────
    builder
      .addCase(saAssignTenantPlan.pending, state => {
        state.tenantActionLoading = true
      })
      .addCase(saAssignTenantPlan.fulfilled, state => {
        state.tenantActionLoading = false
      })
      .addCase(saAssignTenantPlan.rejected, state => {
        state.tenantActionLoading = false
      })

    // ── GET ALL PLANS ──────────────────────────────────────────────────────────
    builder
      .addCase(saGetAllPlans.pending, state => {
        state.plansLoading = true
        state.error = null
      })
      .addCase(saGetAllPlans.fulfilled, (state, action) => {
        state.plansLoading = false
        const { status, data, totalCount, totalPages, currentPage } =
          action.payload || {}
        if (status) {
          state.plans = Array.isArray(data) ? data : []
          state.plansTotalCount = totalCount || 0
          state.plansTotalPages = totalPages || null
          state.plansCurrentPage = currentPage || 1
        }
      })
      .addCase(saGetAllPlans.rejected, state => {
        state.plansLoading = false
      })

    // ── GET PLAN DETAILS ───────────────────────────────────────────────────────
    builder
      .addCase(saGetPlanDetails.pending, state => {
        state.plansLoading = true
        state.planDetails = {}
      })
      .addCase(saGetPlanDetails.fulfilled, (state, action) => {
        state.plansLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.planDetails = data || {}
        }
      })
      .addCase(saGetPlanDetails.rejected, state => {
        state.plansLoading = false
      })

    // ── CREATE PLAN ────────────────────────────────────────────────────────────
    builder
      .addCase(saCreatePlan.pending, state => {
        state.planActionLoading = true
      })
      .addCase(saCreatePlan.fulfilled, state => {
        state.planActionLoading = false
      })
      .addCase(saCreatePlan.rejected, state => {
        state.planActionLoading = false
      })

    // ── UPDATE PLAN ────────────────────────────────────────────────────────────
    builder
      .addCase(saUpdatePlan.pending, state => {
        state.planActionLoading = true
      })
      .addCase(saUpdatePlan.fulfilled, state => {
        state.planActionLoading = false
      })
      .addCase(saUpdatePlan.rejected, state => {
        state.planActionLoading = false
      })

    // ── DELETE PLAN ────────────────────────────────────────────────────────────
    builder
      .addCase(saDeletePlan.pending, state => {
        state.planActionLoading = true
      })
      .addCase(saDeletePlan.fulfilled, (state, action) => {
        state.planActionLoading = false
        const { status } = action.payload || {}
        if (status) {
          const { id } = action.meta.arg
          state.plans = state.plans.filter(p => p._id !== id)
          if (state.totalCount > 0) state.totalCount -= 1
        }
      })
      .addCase(saDeletePlan.rejected, state => {
        state.planActionLoading = false
      })

    // ── UPDATE PLAN STATUS ─────────────────────────────────────────────────────
    builder
      .addCase(saUpdatePlanStatus.pending, state => {
        state.planActionLoading = true
      })
      .addCase(saUpdatePlanStatus.fulfilled, (state, action) => {
        state.planActionLoading = false
        const { status } = action.payload || {}
        if (status) {
          const { id, status: planStatus } = action.meta.arg
          state.plans = state.plans.map(p =>
            p._id === id ? { ...p, status: planStatus } : p
          )
        }
      })
      .addCase(saUpdatePlanStatus.rejected, state => {
        state.planActionLoading = false
      })

    // ── GET ALL SUBSCRIPTIONS ──────────────────────────────────────────────────
    builder
      .addCase(saGetAllSubscriptions.pending, state => {
        state.subscriptionsLoading = true
      })
      .addCase(saGetAllSubscriptions.fulfilled, (state, action) => {
        state.subscriptionsLoading = false
        const { status, data, totalCount, totalPages, currentPage } =
          action.payload || {}
        if (status) {
          state.subscriptions = Array.isArray(data) ? data : []
          state.subscriptionsTotalCount = totalCount || 0
          state.subscriptionsTotalPages = totalPages || null
          state.subscriptionsCurrentPage = currentPage || 1
        }
      })
      .addCase(saGetAllSubscriptions.rejected, state => {
        state.subscriptionsLoading = false
      })

    // ── GET ALL INVOICES ───────────────────────────────────────────────────────
    builder
      .addCase(saGetAllInvoices.pending, state => {
        state.invoicesLoading = true
      })
      .addCase(saGetAllInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false
        const { status, data, totalCount, totalPages, currentPage } =
          action.payload || {}
        if (status) {
          state.invoices = Array.isArray(data) ? data : []
          state.invoicesTotalCount = totalCount || 0
          state.invoicesTotalPages = totalPages || null
          state.invoicesCurrentPage = currentPage || 1
        }
      })
      .addCase(saGetAllInvoices.rejected, state => {
        state.invoicesLoading = false
      })

    // ── GET ALL INTEGRATION CATEGORIES ────────────────────────────────────────
    builder
      .addCase(saGetAllIntegrationCategories.pending, state => {
        state.integrationCategoriesLoading = true
      })
      .addCase(saGetAllIntegrationCategories.fulfilled, (state, action) => {
        state.integrationCategoriesLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.integrationCategories = Array.isArray(data) ? data : []
        }
      })
      .addCase(saGetAllIntegrationCategories.rejected, state => {
        state.integrationCategoriesLoading = false
      })

    // ── CATEGORY ACTIONS ───────────────────────────────────────────────────────
    builder
      .addCase(saCreateIntegrationCategory.pending, state => {
        state.integrationCategoriesActionLoading = true
      })
      .addCase(saCreateIntegrationCategory.fulfilled, state => {
        state.integrationCategoriesActionLoading = false
      })
      .addCase(saCreateIntegrationCategory.rejected, state => {
        state.integrationCategoriesActionLoading = false
      })

    builder
      .addCase(saUpdateIntegrationCategory.pending, state => {
        state.integrationCategoriesActionLoading = true
      })
      .addCase(saUpdateIntegrationCategory.fulfilled, state => {
        state.integrationCategoriesActionLoading = false
      })
      .addCase(saUpdateIntegrationCategory.rejected, state => {
        state.integrationCategoriesActionLoading = false
      })

    builder
      .addCase(saDeleteIntegrationCategory.pending, state => {
        state.integrationCategoriesActionLoading = true
      })
      .addCase(saDeleteIntegrationCategory.fulfilled, state => {
        state.integrationCategoriesActionLoading = false
      })
      .addCase(saDeleteIntegrationCategory.rejected, state => {
        state.integrationCategoriesActionLoading = false
      })

    builder
      .addCase(saUpdateIntegrationCategoryStatus.pending, state => {
        state.integrationCategoriesActionLoading = true
      })
      .addCase(saUpdateIntegrationCategoryStatus.fulfilled, state => {
        state.integrationCategoriesActionLoading = false
      })
      .addCase(saUpdateIntegrationCategoryStatus.rejected, state => {
        state.integrationCategoriesActionLoading = false
      })

    // ── GET ALL INTEGRATIONS ──────────────────────────────────────────────────
    builder
      .addCase(saGetAllIntegrations.pending, state => {
        state.integrationsLoading = true
      })
      .addCase(saGetAllIntegrations.fulfilled, (state, action) => {
        state.integrationsLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.integrations = Array.isArray(data) ? data : []
        }
      })
      .addCase(saGetAllIntegrations.rejected, state => {
        state.integrationsLoading = false
      })

    // ── INTEGRATIONS CATALOG TREE (plans / catalog) ───────────────────────────
    builder
      .addCase(saGetIntegrationsCatalogTree.pending, state => {
        state.integrationCatalogTreeLoading = true
      })
      .addCase(saGetIntegrationsCatalogTree.fulfilled, (state, action) => {
        state.integrationCatalogTreeLoading = false
        const payload = action.payload || {}
        const { data } = payload
        const pickArray = obj => {
          if (!obj) return null
          if (Array.isArray(obj)) return obj
          const keys = [
            "tree",
            "catalog",
            "categories",
            "parents",
            "items",
            "nodes",
            "roots",
            "groups",
            "list",
            "rows",
            "data",
          ]
          for (let i = 0; i < keys.length; i += 1) {
            const k = keys[i]
            if (Array.isArray(obj[k])) return obj[k]
          }
          return null
        }
        const tree = pickArray(data !== undefined ? data : payload)
        state.integrationCatalogTree = Array.isArray(tree) ? tree : []
      })
      .addCase(saGetIntegrationsCatalogTree.rejected, state => {
        state.integrationCatalogTreeLoading = false
        state.integrationCatalogTree = []
      })

    // ── INTEGRATION ACTIONS ───────────────────────────────────────────────────
    builder
      .addCase(saCreateIntegration.pending, state => {
        state.integrationsActionLoading = true
      })
      .addCase(saCreateIntegration.fulfilled, state => {
        state.integrationsActionLoading = false
      })
      .addCase(saCreateIntegration.rejected, state => {
        state.integrationsActionLoading = false
      })

    builder
      .addCase(saUpdateIntegration.pending, state => {
        state.integrationsActionLoading = true
      })
      .addCase(saUpdateIntegration.fulfilled, state => {
        state.integrationsActionLoading = false
      })
      .addCase(saUpdateIntegration.rejected, state => {
        state.integrationsActionLoading = false
      })

    builder
      .addCase(saDeleteIntegration.pending, state => {
        state.integrationsActionLoading = true
      })
      .addCase(saDeleteIntegration.fulfilled, state => {
        state.integrationsActionLoading = false
      })
      .addCase(saDeleteIntegration.rejected, state => {
        state.integrationsActionLoading = false
      })

    builder
      .addCase(saUpdateIntegrationStatus.pending, state => {
        state.integrationsActionLoading = true
      })
      .addCase(saUpdateIntegrationStatus.fulfilled, state => {
        state.integrationsActionLoading = false
      })
      .addCase(saUpdateIntegrationStatus.rejected, state => {
        state.integrationsActionLoading = false
      })
  },
})

export const { clearTenantDetails, clearPlanDetails } = superAdminSlice.actions

export default superAdminSlice.reducer
